
import React, { useState } from 'react';
import { ShieldCheck, Lock, Terminal, Loader2, KeyRound, AlertTriangle } from 'lucide-react';
import { db } from './supabase.ts';
import { UserRole } from './types.ts';

export const MasterLoginPage = ({ onLogin, onCancel }: { onLogin: (u: any) => void, onCancel: () => void }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleMasterAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Autenticação via Supabase
      const user = await db.signIn(email, password);
      
      // Validação exclusiva para Super Admin
      if (user && user.role === UserRole.SUPER_ADMIN) {
        onLogin(user);
      } else if (!user) {
        setError('E-mail ou senha incorretos.');
      } else {
        setError('Acesso negado: Este terminal requer privilégios de Super Admin.');
      }
    } catch (err: any) {
      setError(err.message || 'Credenciais Master Inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080c14] flex items-center justify-center p-6 font-mono selection:bg-primary selection:text-white">
      <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #00a8e0 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
      
      <div className="w-full max-w-md z-10 space-y-8 animate-in fade-in duration-500">
        <div className="text-center space-y-4">
          <div className="inline-flex p-6 bg-primary/10 rounded-[32px] text-primary border border-primary/20 shadow-[0_0_50px_rgba(0,168,224,0.1)]">
            <ShieldCheck size={48} strokeWidth={1.5} />
          </div>
          <div>
             <h1 className="text-2xl font-black text-white uppercase tracking-[0.3em]">Core Auth</h1>
             <p className="text-[10px] text-primary font-bold uppercase tracking-[0.4em] mt-2 italic">Acesso Restrito à Infraestrutura</p>
          </div>
        </div>

        <div className="bg-[#111827] border border-white/10 rounded-[40px] p-8 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent animate-pulse"></div>
          
          <form onSubmit={handleMasterAuth} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[9px] font-black text-white/30 uppercase tracking-widest ml-4">System Identity</label>
                <div className="relative group">
                  <Terminal className="absolute left-6 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-primary transition-colors" size={18} />
                  <input 
                    required 
                    type="email" 
                    className="w-full bg-black/40 border border-white/5 p-5 pl-14 rounded-2xl text-white outline-none focus:border-primary transition-all text-sm"
                    placeholder="master@system.core"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[9px] font-black text-white/30 uppercase tracking-widest ml-4">Access Token</label>
                <div className="relative group">
                  <Lock className="absolute left-6 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-primary transition-colors" size={18} />
                  <input 
                    required 
                    type="password" 
                    className="w-full bg-black/40 border border-white/5 p-5 pl-14 rounded-2xl text-white outline-none focus:border-primary transition-all text-sm"
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-xl flex items-center gap-3 text-red-400 animate-shake">
                <AlertTriangle size={18} />
                <span className="text-[10px] font-bold uppercase tracking-widest">{error}</span>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <button 
                disabled={loading}
                className="w-full bg-primary text-white py-5 rounded-2xl font-black uppercase text-xs tracking-[0.2em] shadow-[0_0_30px_rgba(0,168,224,0.3)] hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <KeyRound size={18} />}
                {loading ? 'Verificando...' : 'Autenticar no Core'}
              </button>
              <button 
                type="button"
                onClick={onCancel}
                className="w-full text-[9px] font-black text-white/20 uppercase tracking-widest hover:text-white transition-colors py-2"
              >
                Abortar Missão
              </button>
            </div>
          </form>
        </div>

        <div className="text-center">
           <p className="text-[8px] font-bold text-white/10 uppercase tracking-[0.5em]">EventLog SaaS Controller v4.2.0-STABLE</p>
        </div>
      </div>
    </div>
  );
};
